import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { LaravelResourceResponse } from '../../../../../_base-shared/contracts/laravel-response.interface';
import { Role } from '../../../../../_base-shared/models/Role';
import { User } from '../../../../../_base-shared/models/User/User';
import { UserExternalService } from '../../../../../_base-shared/models/User/UserExternalService';
import { UserServiceIntegration } from '../../../../../_base-shared/models/User/UserServiceIntegration';
import { MainBaseApiService } from '../../_shared/services/main-base-api.service';

@Injectable({
  providedIn: 'root',
})
export class UserService extends MainBaseApiService {

  public index(requestData = {} as any, relations: Array<string> = []): Observable<LaravelResourceResponse<Array<User>>> {
    requestData.with = relations;
    const params     = MainBaseApiService.convertFiltersForRequest(requestData, 'get');
    return this.http.get<LaravelResourceResponse<Array<User>>>(this.apiUrl + '/users', {params})
      .pipe(catchError(response => this.handleError(response)));
  }

  public get(userId: number, requestData = {} as any, relations: Array<string> = []): Observable<LaravelResourceResponse<User>> {
    requestData.with = relations;
    const params     = MainBaseApiService.convertFiltersForRequest(requestData, 'get');
    return this.http.get<LaravelResourceResponse<User>>(this.apiUrl + '/users/' + userId, {params})
      .pipe(catchError(response => this.handleError(response)));
  }

  public store(data): Observable<LaravelResourceResponse<User>> {
    return this.http.post<LaravelResourceResponse<User>>(this.apiUrl + '/users', data)
      .pipe(catchError(response => this.handleError(response)));
  }

  public update(userId, data): Observable<LaravelResourceResponse<User>> {
    return this.http.patch<LaravelResourceResponse<User>>(this.apiUrl + '/users/' + userId, data)
      .pipe(catchError(response => this.handleError(response)));
  }

  public delete(userId): Observable<LaravelResourceResponse<User>> {
    return this.http.delete<LaravelResourceResponse<User>>(this.apiUrl + '/users/' + userId)
      .pipe(catchError(response => this.handleError(response)));
  }

  public indexRoles(): Observable<LaravelResourceResponse<Array<Role>>> {
    return this.http.get<LaravelResourceResponse<Array<Role>>>(this.apiUrl + '/roles')
      .pipe(catchError(response => this.handleError(response)));
  }

  public getTasks(userId): Observable<LaravelResourceResponse> {
    return this.http.get<LaravelResourceResponse>(this.apiUrl + '/users/' + userId + '/tasks')
      .pipe(catchError(response => this.handleError(response)));
  }

  public externalServices(): Observable<LaravelResourceResponse<Array<UserExternalService>>> {
    return this.http.get<LaravelResourceResponse<Array<UserExternalService>>>(this.apiUrl + '/external-services')
      .pipe(catchError(response => this.handleError(response)));
  }

  public indexIntegrations(userId: number): Observable<LaravelResourceResponse<Array<UserServiceIntegration>>> {
    return this.http.get<LaravelResourceResponse<Array<UserServiceIntegration>>>(
      this.apiUrl + '/users/' + userId + '/integrations')
      .pipe(catchError(response => this.handleError(response)));
  }

  public removeIntegration(userId: number, integrationId: number): Observable<LaravelResourceResponse<any>> {
    return this.http.delete<LaravelResourceResponse>(this.apiUrl + '/users/' + userId + '/integrations/' + integrationId)
      .pipe(catchError(response => this.handleError(response)));
  }

  public testIntegration(userId: number, integrationId: number): Observable<LaravelResourceResponse<any>> {
    return this.http.post<LaravelResourceResponse>(
      this.apiUrl + '/users/' + userId + '/integrations/' + integrationId + '/test', {})
      .pipe(catchError(response => this.handleError(response)));
  }

  public requestAuthorization(userId: number, serviceId: number): Observable<LaravelResourceResponse<any>> {
    return this.http.post<LaravelResourceResponse>(this.apiUrl + '/users/' + userId + '/authorization/request',
      {external_service_id: serviceId})
      .pipe(catchError(response => this.handleError(response)));
  }

  public confirmAuthorization(userId: number, serviceId, code: string): Observable<LaravelResourceResponse<any>> {
    return this.http.post<LaravelResourceResponse>(this.apiUrl + '/users/' + userId + '/authorization/confirm',
      {external_service_id: serviceId, authorization_code: code})
      .pipe(catchError(response => this.handleError(response)));
  }

  public validateField(field: string, value: string, userId: number | null = null): Observable<LaravelResourceResponse<any>> {
    return this.http.post<LaravelResourceResponse>(this.apiUrl + '/users/validate?field=' + field,
      {field_value: value, existing_model_id: userId})
      .pipe(catchError(response => this.handleError(response)));
  }

  public updateIdCard(userId: number, idCard: string): Observable<LaravelResourceResponse<any>> {
    return this.http.patch<LaravelResourceResponse>(this.apiUrl + '/users/' + userId + '/update-field', {id_card: idCard})
      .pipe(catchError(response => this.handleError(response)));
  }

  public changePassword(userId: number, caseId: number, password: string): Observable<LaravelResourceResponse<any>> {
    return this.http.post<LaravelResourceResponse>(this.apiUrl + '/users/' + userId + '/change-password', {
      password,
      case_id: caseId
    })
      .pipe(catchError(response => this.handleError(response)));
  }

  public getCreditorNegotiators(): Observable<LaravelResourceResponse<User>> {
    return this.http.get<LaravelResourceResponse<User>>(
      this.apiUrl + '/departments/5/users')
      .pipe(catchError(response => this.handleError(response)));
  }
}
